import { FileApiType } from '@/api/shared/types'
import { UploadedFileType } from '@/components/shared/file-uploader'

import { RequestsService } from './requests.service'

const fileHeadCache = new Map<
  string,
  ReturnType<typeof RequestsService.checkIsImageFile>
>()

const getFileHead = (fileLink: string) => {
  const cached = fileHeadCache.get(fileLink)

  if (cached) {
    return cached
  }

  const request = RequestsService.checkIsImageFile(fileLink).catch(error => {
    fileHeadCache.delete(fileLink)
    throw error
  })

  fileHeadCache.set(fileLink, request)

  return request
}

export const checkIsImageFile = async (fileLink: string): Promise<boolean> => {
  try {
    const response = await getFileHead(fileLink)
    const contentType = response.headers?.['content-type']

    return contentType?.startsWith?.('image/') ?? false
  } catch {
    return false
  }
}

export const fetchRemoteFileSize = async (
  fileLink: string
): Promise<number | undefined> => {
  try {
    const response = await getFileHead(fileLink)
    const contentLength = response.headers?.['content-length']
    const size = contentLength ? Number(contentLength) : undefined

    return Number.isFinite(size) ? size : undefined
  } catch {
    return undefined
  }
}

export const fetchRequestFileBlob = async (
  remoteFile: FileApiType
): Promise<File | null> => {
  if (!remoteFile.link || !remoteFile.name) {
    return null
  }

  try {
    const { data } = await RequestsService.getRequestFileBlob(remoteFile.link)

    return new File([data], remoteFile.name, {
      type: data.type || 'application/octet-stream'
    })
  } catch {
    return null
  }
}

export const loadRequestFormFiles = async (
  files: UploadedFileType[]
): Promise<UploadedFileType[]> => {
  return Promise.all(
    files.map(async file => {
      if (file.file.size > 0) {
        return file
      }

      if (!file.url) {
        return file
      }

      const blobFile = await fetchRequestFileBlob({
        link: file.url,
        name: file.file.name
      })

      if (!blobFile) {
        return file
      }

      return {
        ...file,
        file: blobFile,
        url: URL.createObjectURL(blobFile)
      }
    })
  )
}

export const extractFilesForSubmit = async (
  files?: UploadedFileType[]
): Promise<File[]> => {
  if (!files?.length) {
    return []
  }

  const hydratedFiles = await loadRequestFormFiles(files)

  return hydratedFiles.map(file => file.file).filter(file => file.size > 0)
}

const BYTE_UNITS = {
  byte: 'Б',
  kilobyte: 'кБ',
  megabyte: 'мБ'
}

const BYTES_IN_KILOBYTE = 1024
const BYTES_IN_MEGABYTE = BYTES_IN_KILOBYTE * 1024

export const formatFileSize = (fileBytes: number) => {
  if (fileBytes < BYTES_IN_KILOBYTE) {
    return `${fileBytes} ${BYTE_UNITS.byte}`
  }

  if (fileBytes < BYTES_IN_MEGABYTE) {
    const sizeInKilobytes = Math.round(fileBytes / BYTES_IN_KILOBYTE)

    return `${sizeInKilobytes} ${BYTE_UNITS.kilobyte}`
  }

  const sizeInMegabytes = Math.round(fileBytes / BYTES_IN_MEGABYTE)

  return `${sizeInMegabytes} ${BYTE_UNITS.megabyte}`
}
